import { ROUTE_PATH } from '@/constants/route-path';
import { WalletContext } from '@/contexts/wallet-context';
import { IPostData } from '@/interfaces/api/post';
import IcBullBear from '@/modules/AlphaPWA/Games/BullAndBear/assets/icBullBear';
import {
  BullBearSymbol,
  SYMBOL_INFOS,
} from '@/modules/AlphaPWA/Games/BullAndBear/formCreate';
import ModalBullBear from '@/modules/AlphaPWA/Games/BullAndBear/modalBullBear';
import Avatar from '@/modules/AlphaPWA/Profiles/TradeKey/components/avatar';
import { labelAmountOrNumberAdds } from '@/modules/AlphaPWA/Profiles/TradeKey/constants';
import { TWEET_DETAIL_MODAL } from '@/modules/AlphaPWA/TweetDetail';
import CBullBearAPI from '@/services/classes/bullBear';
import {
  IDetailBullBear,
  IRewardBullBear,
} from '@/services/interfaces/bullBear';
import { getReferralCode } from '@/services/referral';
import { closeModal, openModal } from '@/state/modal';
import { abbreviateNumber, formatCurrency } from '@/utils';
import useAnalyticsEventTracker, { AlphaActions } from '@/utils/ga';
import { getLink, getUrlAvatarTwitter } from '@/utils/helpers';
import { Button, Flex, Text } from '@chakra-ui/react';
import clsx from 'classnames';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import IcTwitterBase64 from './icTwitterBase64';
import s from './styles.module.scss';
import {
  convertOwnerCircle,
  convertTokenToUSDTForBullBear,
  isPlayWithPassToken,
} from '@/modules/AlphaPWA/Games/BullAndBear/formJoin';
import moment from 'moment';
import AppLoading from '@/components/AppLoading';
import Countdown from '@/components/ActivityTopBar/Countdown';

type Props = {
  data: IPostData;
  isOwner?: boolean;
  isCircle?: boolean;
};

const BullBearPost: React.FC<Props> = ({ data, isOwner, isCircle }) => {
  const dispatch = useDispatch();
  const apiBullBear = useRef(new CBullBearAPI()).current;
  const router = useRouter();
  const { btc2Usd, addressL2 } = useContext(WalletContext);
  const [detail, setDetail] = useState<IDetailBullBear | null>(null);
  const gaEventTracker = useAnalyticsEventTracker();
  const [referralCode, setReferralCode] = useState(null);
  const [myReward, setMyReward] = useState<IRewardBullBear>();

  useEffect(() => {
    getDetail();
    getReferralCode().then(rs => setReferralCode(rs));
  }, []);

  const getDetail = async () => {
    try {
      const response = await Promise.all([
        apiBullBear.getDetailBullAndBear(data?.bull_bear_event_id),
        apiBullBear.getBullAndBearRewards(
          data?.bull_bear_event_id,
          addressL2 as string
        ),
      ]);

      setDetail(response[0]);
      setMyReward(response?.[1]?.[0] as IRewardBullBear);
    } catch (error) {}
  };

  const onNewMrMarket = () => {
    const id = 'create_new_mr_market';
    const onClose = () => dispatch(closeModal({ id }));
    return dispatch(
      openModal({
        id: id,
        theme: 'dark',
        modalProps: {
          centered: true,
          zIndex: 9999999,
        },
        hideCloseButton: false,
        render: () => <ModalBullBear onClose={onClose} />,
      })
    );
  };

  const onShareTW = () => {
    try {
      if (referralCode) {
        let content = `I'm bullish on @NewBitcoinCity.\nIt's not your average SoFi app.\nIt offers a wealth of community engagement and fun activities, from gifting red packets to making collective market predictions and playing games.\nReady to join the excitement?\nFind us here:`;

        if (myReward) {
          content = `I'm bullish on @NewBitcoinCity\nI just won ${
            isPlayWithPassToken(detail)
              ? `${abbreviateNumber(
                  myReward.amount
                )} token${labelAmountOrNumberAdds(myReward.amount as any)}`
              : `${formatCurrency(
                  myReward.amount,
                  2,
                  2,
                  'BTC'
                )} key${labelAmountOrNumberAdds(myReward.amount as any)}`
          }  for correctly predicting the market alongside other fellow members. But it's not just about the rewards; it's the vibrant community discussions, engagements, and the thrill of the game.\nAre you ready to join the excitement?\nFind us here:`;
        }

        const shareUrl = getLink(referralCode);
        const hashtags = 'NewBitcoinCity,Bitcoin';

        gaEventTracker(AlphaActions.ShareBullBear, addressL2);
        window.open(
          `https://twitter.com/intent/tweet?url=${shareUrl}&text=${encodeURIComponent(
            content
          )}&hashtags=${hashtags}`,
          '_blank'
        );
      }
    } catch (error) {}
  };

  const onTapToPlay = () => {
    dispatch(closeModal({ id: TWEET_DETAIL_MODAL }));
    return router.push(`${ROUTE_PATH.ALPHA_MOBILE_BULL_BEAR}/${data?.id}`);
  };

  const renderContent = () => {
    if (!detail) {
      return (
        <>
          <AppLoading />
        </>
      );
    }
    if (moment(detail?.expired_at).diff(moment.now()) <= 0) {
      return (
        <>
          <Flex className={s.fadeContentContainer}>
            <Flex className={s.fadeContent}>
              <Flex onClick={onTapToPlay} flexDirection={'column'}>
                <Flex className={s.fadeItemHeader}>
                  <Text className={s.fadeItemHeaderTitle}>Prize pool</Text>
                  <Text className={s.fadeItemHeaderBTC}>
                    {formatCurrency(detail?.pot_amount, 0, 0, 'BTC')}{' '}
                    <BullBearSymbol
                      isCreateWithToken={isPlayWithPassToken(detail)}
                      ownerCircle={convertOwnerCircle(detail)}
                      size={28}
                    />
                  </Text>
                  <Text className={s.fadeItemHeaderUSD}>
                    $
                    {formatCurrency(
                      convertTokenToUSDTForBullBear(detail, detail?.pot_amount),
                      2,
                      2
                    )}
                  </Text>
                </Flex>

                <Flex className={s.fadeItem}>
                  <Text className={s.fadeItemLabel}>Market</Text>
                  <Text className={s.fadeItemValue}>
                    {SYMBOL_INFOS?.[`${detail?.symbol}`]?.symbol}
                  </Text>
                </Flex>
                <Flex className={s.fadeItem}>
                  <Text className={s.fadeItemLabel}>Joined</Text>
                  <Text className={s.fadeItemValue}>
                    {formatCurrency(detail?.total_ticket, 0, 0)}
                  </Text>
                </Flex>
                <Flex className={s.fadeItem}>
                  <Text className={s.fadeItemLabel}>Winners</Text>
                  <Text className={s.fadeItemValue}>
                    {formatCurrency(detail?.num_ticket_win, 0, 0)}
                  </Text>
                </Flex>
                {parseFloat(myReward?.amount || '0') > 0 && (
                  <Flex className={s.fadeItem}>
                    <Text className={s.fadeItemLabel}>Your earnings</Text>
                    <Flex alignItems={'center'} gap={'8px'}>
                      <Avatar
                        width={32}
                        url={getUrlAvatarTwitter(
                          detail?.circle_twitter?.twitter_avatar as string
                        )}
                        fontSize={16}
                        address={detail?.circle_twitter?.address}
                        name={detail?.circle_twitter?.twitter_name}
                        className={s.avatarGroup}
                      />
                      <Flex
                        justifyContent={'space-between'}
                        flexDirection={'column'}
                      >
                        <Text
                          style={{
                            fontSize: '12px',
                            textAlign: 'left',
                            opacity: 0.7,
                          }}
                          className={s.fadeItemLabel}
                        >
                          {detail?.circle_twitter?.twitter_name}
                        </Text>
                        <Text
                          style={{
                            fontSize: '14px',
                            lineHeight: '100%',
                          }}
                          className={s.fadeItemValue}
                        >
                          {formatCurrency(myReward?.amount, 2, 2, 'BTC')}{' '}
                          {isPlayWithPassToken(detail) ? 'token' : 'key'}
                          {labelAmountOrNumberAdds(myReward?.amount as any)}
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                )}
                {isCircle && !isPlayWithPassToken(detail) && (
                  <Flex className={clsx(s.fadeItem, s.fadeItemNoBorder)}>
                    <Text className={s.fadeItemLabel}>
                      10% reserved for next round
                    </Text>
                    <Text
                      display={'flex'}
                      alignItems={'center'}
                      gap={1}
                      className={s.fadeItemValue}
                    >
                      {formatCurrency(
                        parseFloat(detail?.pot_amount?.toString() || '0') * 0.1,
                        2,
                        2,
                        'BTC'
                      )}{' '}
                      <BullBearSymbol
                        isCreateWithToken={isPlayWithPassToken(detail)}
                        ownerCircle={convertOwnerCircle(detail)}
                        size={20}
                      />
                    </Text>
                  </Flex>
                )}
              </Flex>
              {isCircle && (
                <>
                  {isOwner ? (
                    <Flex
                      style={{
                        justifyContent: 'center',
                      }}
                      className={s.fadeItem}
                      gap={'8px'}
                    >
                      {isPlayWithPassToken(detail) && (
                        <Button
                          onClick={onNewMrMarket}
                          className={s.btnCreateMrMarket}
                        >
                          Create new Mr. Market
                        </Button>
                      )}

                      <Button
                        onClick={onShareTW}
                        className={clsx(s.btnCreateMrMarket, s.btnShare)}
                      >
                        Share on <IcTwitterBase64 />
                      </Button>
                    </Flex>
                  ) : (
                    <Flex
                      style={{
                        justifyContent: 'center',
                      }}
                      className={s.fadeItem}
                    >
                      <Button
                        onClick={onShareTW}
                        className={clsx(s.btnCreateMrMarket, s.btnShare)}
                      >
                        Share on <IcTwitterBase64 />
                      </Button>
                    </Flex>
                  )}
                </>
              )}
            </Flex>

            {!isCircle && (
              <Flex>
                <Button onClick={onShareTW} className={clsx(s.btnShareInFeed)}>
                  Share on <IcTwitterBase64 />
                </Button>
              </Flex>
            )}
          </Flex>
        </>
      );
    }
    return (
      <Flex className={s.fadeContentContainer}>
        <Flex onClick={onTapToPlay} className={s.fadeContent}>
          <Flex className={s.fadeItemHeader}>
            <Text className={s.fadeItemHeaderTitle}>Prize pool</Text>
            <Text className={s.fadeItemHeaderBTC}>
              {formatCurrency(detail?.pot_amount, 0, 0, 'BTC')}{' '}
              <BullBearSymbol
                isCreateWithToken={isPlayWithPassToken(detail)}
                ownerCircle={convertOwnerCircle(detail)}
                size={28}
              />
            </Text>
            <Text className={s.fadeItemHeaderUSD}>
              $
              {formatCurrency(
                convertTokenToUSDTForBullBear(detail, detail?.pot_amount),
                2,
                2
              )}
            </Text>
          </Flex>
          <Flex className={s.fadeItem}>
            <Text className={s.fadeItemLabel}>Market</Text>
            <Text className={s.fadeItemValue}>
              {SYMBOL_INFOS?.[`${detail?.symbol}`]?.symbol}
            </Text>
          </Flex>
          <Flex className={s.fadeItem}>
            <Text className={s.fadeItemLabel}>Joined</Text>
            <Text className={s.fadeItemValue}>
              {formatCurrency(detail?.total_ticket, 0, 0)}
            </Text>
          </Flex>
          <Flex className={s.fadeItem}>
            <Text className={s.fadeItemLabel}>🔥 Time left</Text>
            <Text className={clsx(s.fadeItemValue, s.fadeItemTimer)}>
              <Countdown
                expiredTime={detail?.expired_at as string}
                onRefreshEnd={getDetail}
              />
            </Text>
          </Flex>
        </Flex>
        <Flex>
          <Button onClick={onTapToPlay} className={clsx(s.btnShareInFeed)}>
            Tap to play
          </Button>
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <Flex
        className={s.container}
        style={{
          minHeight: myReward ? '420px' : '350px',
        }}
      >
        <IcBullBear show={isCircle ? 0 : 1} />
        {renderContent()}
      </Flex>
    </>
  );
};

export default BullBearPost;
